import { carouselProductsContainer } from "@eisinc/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductIdsForCompare,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { createRef, ReactNode, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
}

const mapStateToProps = (state: ApplicationState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
});

const mapDispatchToProps = {
    removeProductIdToCompare,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasShellContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions { }

export interface CarouselStyles {
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
}

export const carouselStyles: CarouselStyles = {
    attributeLabelGridItem: {
        width: 12,
        css: css`
            padding: 10px 5px;
            &:nth-child(even) {
                background-color: ${getColor("common.accent")};
            }

            .carousel-products-container {
                padding: 20px 10px;
            }
        `,
    },
    attributeLabelText: {
        ellipsis: true,
        variant: "h6",
    },
    attributeValueGridItem: {
        width: 12,
        css: css`
            border: 0 !important;
            padding: 10px;
            &:nth-child(even) {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
        css: css`
            display: block;
        `,
    },
};

const productCarouselStyle: ProductCarouselProductStyles = {
    customActionStyle: {
        css: css`
            position: absolute;
            top: 5px;
            right: 5px;
        `,
    },
};

const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    slideDetails,
    removeProductIdToCompare,
    products,
}: Props) => {
    React.useEffect(() => {
        // eslint-disable-next-line global-require
        require("swiper/swiper-bundle.min.css");
    }, []);

    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue.map(o => o.valueDisplay).join(", ")}
                </Typography>
            );
        }

        return <>&nbsp;</>;
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    return (
        <>
            {products?.length && (
                <StyledWrapper css={css`
                width: 100%;
                max-width: 100%;
            `}>
                    <StyledWrapper className="carousel-products-container" css={carouselProductsContainer}>
                        <Swiper
                            spaceBetween={40}
                            touchRatio={0}
                            navigation={{
                                nextEl: ".swiper-button-next",
                                prevEl: ".swiper-button-prev",
                            }}
                            className="swiper-carousel"
                            breakpoints={{
                                // when window width is >= 640px
                                0: {
                                    slidesPerView: 1,
                                },
                                // when window width is >= 768px
                                700: {
                                    slidesPerView: 2,
                                },
                                // when window width is >= 768px
                                1100: {
                                    slidesPerView: 3,
                                },
                                // when window width is >= 768px
                                1439: {
                                    slidesPerView: 4,
                                },
                            }}
                        >
                            {products?.map((product, index: any) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <SwiperSlide key={`carouselProduct${id}${index}`}>
                                    <ProductCarouselProduct
                                        carouselId={id}
                                        product={product}
                                        showImage={true}
                                        showBrand={true}
                                        showTitle={true}
                                        showPartNumbers={true}
                                        showPrice={true}
                                        showAddToCart={true}
                                        showAddToList={true}
                                        view="carousel"
                                        hideAvailability={false}
                                        showElipsesOn="3"
                                        extendedStyles={productCarouselStyle}
                                        customActions={true}
                                        customActionsTemplate={(product: ProductModel) => {
                                            return (
                                                <Clickable
                                                    onClick={() => removeProductIdToCompare({ productId: product.id })}
                                                    data-test-selector="productCompareRemoveProductButton"
                                                    data-test-key={product.id}
                                                >
                                                    <VisuallyHidden>
                                                        {translate(
                                                            "Remove Product {0} from Compare",
                                                            product.productTitle,
                                                        )}
                                                    </VisuallyHidden>
                                                    <Icon size={18} src={X} />
                                                </Clickable>
                                            );
                                        }}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <StyledWrapper className="swiper-navigation">
                            <div className="swiper-button-prev"></div>
                            <div className="swiper-button-next"></div>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper>
                        {slideDetails.attributeTypes.map(attribute => (
                            <StyledWrapper key={attribute.id} {...styles.attributeLabelGridItem}>
                                <Typography
                                    id={`carousel-${id}_attribute-label-${attribute.id}`}
                                    {...styles.attributeLabelText}
                                >
                                    {attribute.label || attribute.name}
                                </Typography>
                                <StyledWrapper>
                                    <StyledWrapper
                                        className="carousel-products-container"
                                        css={carouselProductsContainer}
                                    >
                                        <Swiper
                                            spaceBetween={40}
                                            touchRatio={0}
                                            navigation={{
                                                nextEl: ".swiper-button-next",
                                                prevEl: ".swiper-button-prev",
                                            }}
                                            className="swiper-carousel"
                                            breakpoints={{
                                                // when window width is >= 640px
                                                0: {
                                                    slidesPerView: 1,
                                                },
                                                // when window width is >= 768px
                                                700: {
                                                    slidesPerView: 2,
                                                },
                                                // when window width is >= 768px
                                                1100: {
                                                    slidesPerView: 3,
                                                },
                                                // when window width is >= 768px
                                                1439: {
                                                    slidesPerView: 4,
                                                },
                                            }}
                                        >
                                            {products?.map((product, index: any) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <SwiperSlide key={`carouselProduct-attribute${id}${index}`}>
                                                    <StyledWrapper
                                                        key={`${product.id}-attribute-${attribute.id}`}
                                                        {...styles.attributeValueGridItem}
                                                    >
                                                        {getProductAttributeValue(product, attribute)}
                                                    </StyledWrapper>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(Carousel)));
